import * as React from "react"
import { useRouteMatch } from "react-router-dom"
import { Flow, Interval } from "@digits-graphql/frontend/graphql-bearer"
import { chevronStyles, PointingDirection } from "@digits-shared/components/UI/Elements/Chevron"
import { ensureArray } from "@digits-shared/helpers/arrayHelper"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import numberHelper, { CurrencyStyle } from "@digits-shared/helpers/numberHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { type TimeseriesValue } from "src/frontend/components/Shared/Layout/Components/Charts/toTimeseries"
import {
  useTimeseriesToDisplay,
  useTimeSeriesTotal,
} from "src/frontend/components/Shared/Layout/Components/Headers/headersHelpers"
import { type ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { type ComponentSizeProps } from "src/frontend/components/Shared/Layout/types"
import { useIsEditLayoutActive } from "src/frontend/components/Shared/Portals/State/useIsEditLayoutActive"
import routes from "src/frontend/routes"

/*
  STYLES
*/

const Summary = styled.div<ComponentSizeProps & { isEditLayoutActive: boolean }>`
  display: grid;
  grid-template-areas:
    "header    menu"
    "subheader none";
  grid-template-columns: 1fr auto;
  margin-bottom: ${({ componentSize }) => (componentSize.isExtraLarge ? "26px" : "16px")};
  margin-right: ${({ isEditLayoutActive }) => (isEditLayoutActive ? "10px" : "0")};
`

const Header = styled.header`
  grid-area: header;
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  height: 28px;
  gap: 6px;
  align-items: center;

  &:has(> *:only-child) {
    gap: 0;
  }
`

const ComponentTitle = styled.div<ComponentSizeProps>`
  position: relative;
  flex: 1;

  font-size: ${({ componentSize }) => componentSize.headerSize};
  font-weight: ${fonts.weight.heavy};
  margin-bottom: 2px;

  display: flex;
  gap: 6px;
  align-items: center;
  align-self: center;

  color: ${colors.secondary};
  @media print {
    & {
      color: ${colors.black};
    }
  }
`

const TitleWrap = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: -webkit-fill-available;

  // since overflow is hidden the focus-visible outline on the child
  // is clipped, so propagate the outline to the title wrap instead
  &:has(> *:only-child:focus-visible) {
    outline: var(--outline);
  }
`

const Date = styled.div`
  color: ${colors.secondary80};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  @media print {
    & {
      color: ${colors.regentGray};
    }
  }
`

const SubHeader = styled.div<ComponentSizeProps>`
  grid-area: subheader;

  color: ${colors.secondary50};
  @media print {
    & {
      color: ${colors.regentGray};
    }
  }

  font-size: ${({ componentSize }) => componentSize.subHeaderSize};
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px;
`

const HeaderValue = styled.div<ComponentSizeProps>`
  color: ${colors.secondary};
  @media print {
    & {
      color: ${colors.black};
    }
  }

  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin-left: auto;
  gap: 4px;
  font-weight: ${fonts.weight.heavy};
  font-size: ${({ componentSize }) => componentSize.headerSize};
  margin-bottom: 2px;
`

const ValueMenu = styled.div`
  grid-area: menu;
  margin-left: 6px;
  align-self: center;
`

const Chevron = styled.div<{ direction: PointingDirection; color: string } & ComponentSizeProps>`
  ${({ color, direction, componentSize }) =>
    chevronStyles(direction, componentSize.chevronSize, color)};

  position: absolute;
  top: ${({ componentSize }) => componentSize.chevronTop};
  left: ${({ componentSize }) => componentSize.chevronLeft};
`

const Delta = styled.div`
  position: relative;
`

const DeltaPercent = styled.div<{ color: string } & ComponentSizeProps>`
  color: ${({ color }) => color};
  margin-left: 8px;
`

/*
  INTERFACES
*/

interface ComponentSummaryProps {
  className?: string
  componentSize: ComponentSize
  title: React.ReactNode | undefined
  icon?: React.ReactNode
  timeseries: TimeseriesValue[] | undefined
  defaultValueNode?: React.ReactNode
  selectedIndex?: number | number[]
  subHeaderCustomNode?: React.ReactNode
  dateCustomNode?: React.ReactNode
  isEstimate?: boolean
  noDelta?: boolean
  valueMenu?: React.ReactNode
}

interface ComponentAmountProps {
  timeseriesToDisplay: TimeseriesValue[] | TimeseriesValue
  isEstimate?: boolean
}

interface ComponentDateProps {
  timeseriesToDisplay: TimeseriesValue[] | TimeseriesValue
  dateCustomNode?: React.ReactNode
}

interface ComponentTitleProps {
  className?: string
  componentSize: ComponentSize
  title: React.ReactNode | undefined
  icon?: React.ReactNode
  periodName: string | undefined
}

interface PeriodDeltaProps {
  timeseriesToDisplay: TimeseriesValue[] | TimeseriesValue
}

/*
  COMPONENTS
*/

export const ComponentSummary: React.FC<ComponentSummaryProps> = ({
  className,
  componentSize,
  title,
  icon,
  timeseries,
  defaultValueNode,
  selectedIndex,
  isEstimate,
  noDelta = false,
  subHeaderCustomNode,
  dateCustomNode,
  valueMenu,
}) => {
  const isEditLayoutActive = useIsEditLayoutActive()
  const timeseriesToDisplay = useTimeseriesToDisplay(timeseries, selectedIndex)

  const noActivity = React.useMemo(
    () => timeseries?.every((value) => value.moneyFlow.value.amount === 0),
    [timeseries]
  )

  const showCustomDefault =
    (selectedIndex === undefined || noDelta) && defaultValueNode !== undefined

  return (
    <Summary
      className={className}
      componentSize={componentSize}
      isEditLayoutActive={isEditLayoutActive}
    >
      <Header>
        <ComponentTitle componentSize={componentSize}>
          {icon}
          <TitleWrap>{title}</TitleWrap>
        </ComponentTitle>
        <HeaderValue componentSize={componentSize}>
          {showCustomDefault
            ? defaultValueNode
            : timeseriesToDisplay &&
              !noActivity && (
                <ComponentAmount
                  timeseriesToDisplay={timeseriesToDisplay}
                  isEstimate={isEstimate}
                />
              )}
        </HeaderValue>
      </Header>
      {timeseriesToDisplay && (
        <SubHeader componentSize={componentSize}>
          <ComponentDate
            timeseriesToDisplay={timeseriesToDisplay}
            dateCustomNode={dateCustomNode}
          />
          {showCustomDefault && subHeaderCustomNode}
          {!noDelta && !showCustomDefault && (
            <PeriodDelta timeseriesToDisplay={timeseriesToDisplay} componentSize={componentSize} />
          )}
        </SubHeader>
      )}
      {valueMenu && <ValueMenu>{valueMenu}</ValueMenu>}
    </Summary>
  )
}

export const ComponentDateSummary: React.FC<ComponentSummaryProps> = ({
  className,
  componentSize,
  title,
  icon,
  timeseries,
  selectedIndex,
  subHeaderCustomNode,
  dateCustomNode,
  valueMenu,
}) => {
  const isEditLayoutActive = useIsEditLayoutActive()
  const timeseriesToDisplay = useTimeseriesToDisplay(timeseries, selectedIndex)

  return (
    <Summary
      className={className}
      componentSize={componentSize}
      isEditLayoutActive={isEditLayoutActive}
    >
      <Header>
        <ComponentTitle componentSize={componentSize}>
          {icon}
          <TitleWrap>{title}</TitleWrap>
        </ComponentTitle>
      </Header>
      {timeseriesToDisplay && (
        <SubHeader componentSize={componentSize}>
          <ComponentDate
            timeseriesToDisplay={timeseriesToDisplay}
            dateCustomNode={dateCustomNode}
          />
          {subHeaderCustomNode}
        </SubHeader>
      )}
      {valueMenu && <ValueMenu>{valueMenu}</ValueMenu>}
    </Summary>
  )
}

export const ComponentHeaderTitle: React.FCWithRef<HTMLDivElement, ComponentTitleProps> = ({
  ref,
  className,
  componentSize,
  title,
  periodName,
  icon,
}) => (
  <Summary
    className={className}
    componentSize={componentSize}
    isEditLayoutActive={useIsEditLayoutActive()}
  >
    <Header>
      <ComponentTitle componentSize={componentSize}>
        {icon}
        <TitleWrap ref={ref}>{title}</TitleWrap>
      </ComponentTitle>
    </Header>
    <SubHeader componentSize={componentSize}>
      <Date>{periodName}</Date>
    </SubHeader>
  </Summary>
)

export const ComponentDate: React.FC<ComponentDateProps> = ({
  timeseriesToDisplay,
  dateCustomNode,
}) => {
  const isPortalHome = useRouteMatch(routes.legalEntityHome.parameterizedPath)?.isExact
  if (dateCustomNode) return dateCustomNode

  const series = ensureArray(timeseriesToDisplay)
  const first = series[0]
  const last = series[series.length - 1]
  if (!first || !last) return null

  const { period } = first
  const interval = period.interval || Interval.Month
  if (
    isPortalHome &&
    dateTimeHelper.arePeriodsEqual(period, dateTimeHelper.todayPeriodForInterval(interval))
  ) {
    return <Date>as of Today</Date>
  }

  return (
    <Date>
      {dateTimeHelper.displayNameForTimeRange(
        first.period.startedAt,
        last.period.endedAt,
        interval
      )}
    </Date>
  )
}

const ComponentAmount: React.FC<ComponentAmountProps> = ({ timeseriesToDisplay, isEstimate }) => {
  const moneyFlow = useTimeSeriesTotal(timeseriesToDisplay)
  return (
    <>
      {isEstimate ? "~" : ""}
      {moneyFlowHelper.currency(moneyFlow, { style: CurrencyStyle.Detail })}
    </>
  )
}

const PeriodDelta: React.FC<PeriodDeltaProps & ComponentSizeProps> = ({
  timeseriesToDisplay,
  componentSize,
}) => {
  const delta = Array.isArray(timeseriesToDisplay) ? undefined : timeseriesToDisplay.deltaPrevious
  const green = colors.accentTurquoise
  const orange = colors.errorDark

  const numberOptions: Intl.NumberFormatOptions = React.useMemo(
    () => ({
      signDisplay: "never",
    }),
    []
  )

  const amountOptions = React.useMemo(
    () => ({
      ...numberOptions,
      style: CurrencyStyle.Detail,
    }),
    [numberOptions]
  )

  const percentOptions = React.useMemo(
    () => ({
      ...numberOptions,
      style: "percent" as const,
    }),
    [numberOptions]
  )

  const displayPercentage = React.useMemo(() => {
    if (!delta?.moneyFlow.value) return undefined
    const { percentageOfAmount } = delta

    const abs = Math.abs(percentageOfAmount)
    if (abs > 1000) {
      return "Over 1000%"
    }
    if (abs > 0 && abs < 1) {
      return "Under 1%"
    }
    if (abs === 0) return "Stable"

    return numberHelper.numberFormatter(percentOptions).format(percentageOfAmount / 100)
  }, [delta, percentOptions])

  const displayDelta = React.useMemo(() => {
    if (!delta?.moneyFlow.value) return undefined
    const { moneyFlow } = delta
    const amount = numberHelper.currency(moneyFlow.value, amountOptions)

    const isPeriodPositive = moneyFlow.value.amount >= 0
    const color = isPeriodPositive ? green : orange
    const direction =
      (isPeriodPositive && moneyFlow.businessFlow === Flow.Inbound) ||
      (!isPeriodPositive && moneyFlow.businessFlow === Flow.Outbound)
        ? PointingDirection.Up
        : PointingDirection.Down

    return {
      amount,
      color,
      direction,
    }
  }, [delta, amountOptions, green, orange])

  if (!displayDelta) return null

  return (
    <Delta>
      <Chevron
        direction={displayDelta.direction}
        color={displayDelta.color}
        componentSize={componentSize}
      />
      <DeltaPercent color={displayDelta.color} componentSize={componentSize}>
        {displayPercentage}
      </DeltaPercent>
    </Delta>
  )
}
